import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useBudgetGame } from './useBudgetGame';
import { BUDGET_CATEGORIES, CATEGORY_ICONS, BUDGET_TIPS } from './types';
import { formatCurrency } from '@/lib/format';
import { TrendingUp, TrendingDown, Clock, Award, Trophy, Timer, ArrowLeft, CheckCircle, XCircle, Brain, Lightbulb, Plus, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { useToast } from '@/components/ui/use-toast';

export const BudgetGame: React.FC = () => {
  const navigate = useNavigate();
  const { state, startGame, addItem, removeItem, endGame, formatTime, isUpdatingProgress } = useBudgetGame();
  const { toast } = useToast();
  const [newItem, setNewItem] = useState({
    category: '',
    amount: '',
    type: 'income' as 'income' | 'expense'
  });
  const [tipIndex, setTipIndex] = useState(0);
  
  // Mostrar un consejo financiero aleatorio cada 15 segundos
  useEffect(() => {
    if (!state.gameStarted || state.isGameOver) return;
    
    const interval = setInterval(() => {
      setTipIndex(prev => (prev + 1) % BUDGET_TIPS.length);
    }, 15000);
    
    return () => clearInterval(interval);
  }, [state.gameStarted, state.isGameOver]);

  // Mostrar confetti cuando se completa el presupuesto con éxito
  useEffect(() => {
    if (state.isGameOver && state.stats.isBalanced && state.stats.hasSavings) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    }
  }, [state.isGameOver, state.stats.isBalanced, state.stats.hasSavings]);

  // Pantalla de inicio
  const StartScreen = () => (
    <div className="text-center">
      <motion.div 
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="mb-6"
      >
        <h3 className="text-3xl font-bold mb-4 text-primary">¡Aprende a Manejar tu Dinero!</h3>
      </motion.div>
      
      <div className="space-y-4 mb-8 bg-blue-50 p-6 rounded-lg text-left shadow-md">
        <p className="font-semibold text-center mb-4 text-lg">En este juego aprenderás a:</p>
        <div className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm">
          <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
          <span className="font-medium">Planificar tus ingresos y gastos</span>
        </div>
        <div className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm">
          <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
          <span className="font-medium">Ahorrar para el futuro</span>
        </div>
        <div className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm">
          <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
          <span className="font-medium">Tomar decisiones financieras inteligentes</span>
        </div>
      </div>

      <Button 
        onClick={() => startGame()} 
        size="lg"
        className="bg-primary hover:bg-primary-dark text-white font-bold py-4 px-10 rounded-full shadow-lg hover:shadow-xl transition-all text-lg"
      >
        ¡Comenzar!
      </Button>
    </div>
  );

  // Pantalla de juego
  const GameScreen = () => (
    <div className="space-y-6">
      {/* Consejo financiero */}
      <div className="bg-yellow-50 p-3 rounded-lg flex items-start gap-3 border border-yellow-200">
        <Lightbulb className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-sm">Consejo:</p>
          <p className="text-sm text-gray-700">{BUDGET_TIPS[tipIndex]}</p>
        </div>
      </div>

      {/* Instrucciones rápidas */}
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <h4 className="font-semibold mb-2 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-blue-500" />
          Cómo jugar:
        </h4>
        <ol className="list-decimal pl-5 space-y-1 text-sm">
          <li>Añade tus <strong>ingresos</strong> (mesada, regalos)</li>
          <li>Añade tus <strong>gastos</strong> (incluye ahorros)</li>
          <li>Mantén un <strong>balance positivo</strong> (ingresos mayor que gastos)</li>
          <li>¡No olvides <strong>ahorrar</strong>!</li>
        </ol>
      </div>

      {/* Formulario simplificado para agregar items */}
      <Card className="p-4 shadow-md border-2 border-primary">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Plus className="w-5 h-5 text-primary" />
          Agregar a tu presupuesto
        </h3>

        <form onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }} className="grid grid-cols-1 gap-4">
          <div className="flex gap-2">
            <button
              type="button"
              className={`flex-1 py-3 px-4 rounded-md font-medium flex items-center justify-center gap-2 ${
                newItem.type === 'income' 
                  ? 'bg-green-500 text-white hover:bg-green-600' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => setNewItem({ ...newItem, type: 'income', category: '' })}
            >
              <span className="text-xl">💰</span>
              <span>Ingreso</span>
            </button>
            <button
              type="button"
              className={`flex-1 py-3 px-4 rounded-md font-medium flex items-center justify-center gap-2 ${
                newItem.type === 'expense' 
                  ? 'bg-red-500 text-white hover:bg-red-600' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => setNewItem({ ...newItem, type: 'expense', category: '' })}
            >
              <span className="text-xl">💸</span>
              <span>Gasto</span>
            </button>
          </div>

          {newItem.type && (
            <>
              <div className="border border-gray-200 rounded-md p-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">Categoría</label>
                <div className="grid grid-cols-2 gap-3">
                  {BUDGET_CATEGORIES[newItem.type].map((category) => (
                    <button
                      key={category}
                      type="button"
                      className={`flex items-center justify-start gap-2 p-3 border rounded-md transition-colors ${
                        newItem.category === category
                          ? newItem.type === 'income'
                            ? 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200'
                            : 'bg-red-100 text-red-800 border-red-300 hover:bg-red-200'
                          : 'border-gray-300 hover:bg-gray-100'
                      }`}
                      onClick={() => setNewItem({ ...newItem, category })}
                    >
                      <span className="text-xl">{CATEGORY_ICONS[category]}</span>
                      <span className="text-sm font-medium truncate">{category}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="w-full">
                <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-2">
                  Monto
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                  <input
                    id="amount"
                    type="number"
                    placeholder="0"
                    value={newItem.amount}
                    onChange={(e) => setNewItem({ ...newItem, amount: e.target.value })}
                    className="w-full p-3 pl-8 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                    min="1"
                  />
                </div>
              </div>

              <button 
                type="submit"
                className={`w-full py-3 px-4 rounded-md font-medium text-white transition-colors ${
                  !newItem.category || !newItem.amount
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-primary hover:bg-primary-dark'
                }`}
                disabled={!newItem.category || !newItem.amount}
              >
                Agregar
              </button>
            </>
          )}
        </form>
      </Card>

      {/* Resumen del presupuesto */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-4 shadow-md border-t-4 border-t-green-500">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-500" />
            Ingresos
          </h3>
          {state.items.filter(item => item.type === 'income').length === 0 ? (
            <div className="text-center py-6 bg-gray-50 rounded-lg">
              <p className="text-gray-500 text-sm">No has agregado ingresos aún</p>
              <p className="text-xs text-gray-400 mt-1">Ejemplo: Mesada, Regalo de cumpleaños</p>
            </div>
          ) : (
            <div className="space-y-2">
              {state.items
                .filter((item) => item.type === 'income')
                .map((item) => (
                  <motion.div 
                    key={item.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex justify-between items-center p-2 hover:bg-gray-50 rounded group"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{CATEGORY_ICONS[item.category] || '💰'}</span>
                      <span className="font-medium">{item.category}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-green-600 font-medium">{formatCurrency(Number(item.amount))}</span>
                      <button 
                        onClick={() => removeItem(item.id)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-500"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </motion.div>
                ))}
            </div>
          )}
          <div className="mt-4 pt-2 border-t">
            <div className="flex justify-between font-semibold">
              <span>Total Ingresos:</span>
              <span className="text-green-600">
                {formatCurrency(state.totalIncome)}
              </span>
            </div>
          </div>
        </Card>

        <Card className="p-4 shadow-md border-t-4 border-t-red-500">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-red-500" />
            Gastos
          </h3>
          {state.items.filter(item => item.type === 'expense').length === 0 ? (
            <div className="text-center py-6 bg-gray-50 rounded-lg">
              <p className="text-gray-500 text-sm">No has agregado gastos aún</p>
              <p className="text-xs text-gray-400 mt-1">Ejemplo: Ahorro, Dulces, Juguetes</p>
            </div>
          ) : (
            <div className="space-y-2">
              {state.items
                .filter((item) => item.type === 'expense')
                .map((item) => (
                  <motion.div 
                    key={item.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex justify-between items-center p-2 hover:bg-gray-50 rounded group"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{CATEGORY_ICONS[item.category] || '💸'}</span>
                      <span className="font-medium">{item.category}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-red-600 font-medium">{formatCurrency(Number(item.amount))}</span>
                      <button 
                        onClick={() => removeItem(item.id)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-500"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </motion.div>
                ))}
            </div>
          )}
          <div className="mt-4 pt-2 border-t">
            <div className="flex justify-between font-semibold">
              <span>Total Gastos:</span>
              <span className="text-red-600">
                {formatCurrency(state.totalExpenses)}
              </span>
            </div>
          </div>
        </Card>
      </div>

      {/* Balance y progreso */}
      <Card className="p-4 shadow-md bg-gradient-to-r from-blue-50 to-purple-50">
        <h3 className="text-lg font-semibold mb-4">Tu Balance</h3>
        
        <div className="flex flex-col sm:flex-row justify-between items-center mb-4 sm:mb-6 gap-4">
          <div className="text-center sm:text-left">
            <p className="text-sm text-gray-600">Balance Total:</p>
            <p className={`text-2xl font-bold ${state.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(state.balance)}
            </p>
          </div>
          
          <div className="flex flex-col items-center gap-2">
            <div className="flex items-center gap-2">
              <div className={`w-4 h-4 rounded-full ${state.stats.isBalanced ? 'bg-green-500' : 'bg-gray-300'}`}></div>
              <span className="text-sm">Presupuesto balanceado</span>
            </div>
            <div className="flex items-center gap-2">
              <div className={`w-4 h-4 rounded-full ${state.stats.hasSavings ? 'bg-green-500' : 'bg-gray-300'}`}></div>
              <span className="text-sm">Incluye ahorros</span>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-3 rounded-lg">
          <div className="flex justify-between text-sm mb-1">
            <span className="font-medium text-red-500">Gastos: {formatCurrency(state.totalExpenses)}</span>
            <span className="font-medium text-green-500">Ingresos: {formatCurrency(state.totalIncome)}</span>
          </div>
          <div className="relative h-6 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="absolute top-0 left-0 h-full bg-green-500"
              style={{ width: `${state.totalIncome > 0 ? Math.min((state.totalIncome / (state.totalIncome + state.totalExpenses)) * 100, 100) : 0}%` }}
            ></div>
            <div className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white">
              {state.balance >= 0 ? '¡Bien hecho!' : 'Necesitas más ingresos o menos gastos'}
            </div>
          </div>
        </div>
      </Card>

      {/* Botón para finalizar */}
      <div className="text-center">
        <Button 
          onClick={endGame}
          className="bg-primary hover:bg-primary-dark text-white font-bold py-3 px-6 rounded-full shadow-lg hover:shadow-xl transition-all"
          disabled={isUpdatingProgress}
        >
          Finalizar Presupuesto
        </Button>
      </div>
    </div>
  );

  // Pantalla de resumen final
  const GameSummary = () => {
    const isSuccess = state.stats.isBalanced && state.stats.hasSavings;
    
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center justify-center p-6 bg-white rounded-lg shadow-lg w-full max-w-md mx-auto space-y-6"
      >
        <div className="text-center">
          <div className="mb-4">
            {isSuccess ? (
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-green-500" />
              </div>
            ) : (
              <div className="w-20 h-20 mx-auto bg-blue-100 rounded-full flex items-center justify-center">
                <Award className="w-12 h-12 text-blue-500" />
              </div>
            )}
          </div>
          
          <h2 className="text-2xl font-bold mb-2">
            {isSuccess ? '¡Felicidades! 🎉' : 'Presupuesto Completado ✅'}
          </h2>
          <p className="text-gray-600">
            {isSuccess 
              ? '¡Has creado un presupuesto perfecto! Sabes cómo manejar tu dinero.' 
              : 'Has completado tu presupuesto. Veamos qué tal lo hiciste.'}
          </p>
        </div>

        {/* Puntuación y Cerebrix */}
        <div className="bg-blue-50 w-full rounded-lg p-4">
          <div className="flex justify-between items-center mb-3">
            <span className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-500" />
              <span className="font-medium">Puntos</span>
            </span>
            <span className="text-xl font-bold">{state.score}</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-purple-500" />
              <span className="font-medium">Cerebrix</span>
            </span>
            <span className="text-xl font-bold">{state.cerebrixEarned || 0}</span>
          </div>
        </div>

        {/* Resultados */}
        <div className="w-full space-y-3">
          <div className="flex items-center gap-3">
            {state.stats.isBalanced 
              ? <CheckCircle className="h-6 w-6 text-green-500" /> 
              : <XCircle className="h-6 w-6 text-red-500" />}
            <div>
              <p className="font-medium">Presupuesto Balanceado</p>
              <p className="text-sm text-gray-600">
                {state.stats.isBalanced 
                  ? '¡Genial! Tus ingresos son mayores que tus gastos.' 
                  : 'Tus gastos son mayores que tus ingresos. ¡Cuidado!'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {state.stats.hasSavings 
              ? <CheckCircle className="h-6 w-6 text-green-500" /> 
              : <XCircle className="h-6 w-6 text-red-500" />}
            <div>
              <p className="font-medium">Ahorros</p>
              <p className="text-sm text-gray-600">
                {state.stats.hasSavings 
                  ? '¡Muy bien! Estás ahorrando para el futuro.' 
                  : 'No has incluido ahorros. ¡Es importante ahorrar!'}
              </p>
            </div>
          </div>
        </div>

        {/* Mensaje de aprendizaje */}
        <div className="bg-yellow-50 p-3 rounded-lg">
          <p className="text-sm font-medium">Lo que aprendiste:</p>
          <p className="text-sm text-gray-700 mt-1">
            {isSuccess 
              ? 'Hacer un presupuesto te ayuda a planificar tu dinero y ahorrar para el futuro. ¡Sigue así!' 
              : 'Un buen presupuesto debe tener más ingresos que gastos y siempre incluir ahorros. ¡Inténtalo de nuevo!'}
          </p>
        </div>

        {/* Botones de acción */}
        <div className="flex gap-3 w-full">
          <Button 
            onClick={() => navigate('/games')}
            variant="outline" 
            className="flex-1"
          >
            Volver a Juegos
          </Button>
          <Button 
            onClick={startGame}
            className="flex-1 bg-primary"
          >
            Jugar de Nuevo
          </Button>
        </div>
      </motion.div>
    );
  };

  const handleSubmit = () => {
    if (!newItem.category || !newItem.amount) return;
    
    try {
      // Convertir el monto a número y validar
      const amount = parseFloat(newItem.amount);
      
      if (isNaN(amount) || amount <= 0) {
        toast({
          title: "Error",
          description: "Por favor ingresa un monto válido mayor a cero",
          variant: "destructive"
        });
        return;
      }
      
      // Agregar el item al presupuesto
      addItem({
        type: newItem.type,
        category: newItem.category,
        amount: amount
      });
  
      // Limpiar el formulario pero mantener el tipo seleccionado
      setNewItem({
        category: '',
        amount: '',
        type: newItem.type // Mantener el mismo tipo para facilitar la entrada de datos
      });
      
      // Mostrar confirmación
      toast({
        title: "¡Agregado!",
        description: `${newItem.type === 'income' ? 'Ingreso' : 'Gasto'} agregado correctamente`,
        variant: "default"
      });
    } catch (error) {
      console.error("Error al agregar item:", error);
      toast({
        title: "Error",
        description: "Ocurrió un error al agregar el item",
        variant: "destructive"
      });
    }
  };

  // Determinar qué contenido mostrar
  const content = !state.gameStarted 
    ? <StartScreen /> 
    : state.isGameOver 
      ? <GameSummary /> 
      : <GameScreen />;

  return (
    <div className="w-full max-w-4xl mx-auto p-2 sm:p-6">
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
        {/* Botón Volver */}
        <button
          onClick={() => navigate('/games')}
          className="mb-4 flex items-center gap-2 text-gray-600 hover:text-primary transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Volver a Juegos</span>
        </button>

        {/* Header del juego */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-4 sm:mb-6 gap-2">
          <div className="flex items-center gap-2">
            <h2 className="text-xl sm:text-2xl font-bold">Presupuesto Familiar</h2>
          </div>
          <div className="flex flex-wrap justify-center sm:justify-end items-center gap-3 w-full sm:w-auto">
            {state.gameStarted && !state.isGameOver && (
              <>
                <div className="flex items-center gap-2 min-w-[100px] justify-center">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  <span className="text-base sm:text-lg font-semibold whitespace-nowrap">Puntos: {state.score}</span>
                </div>
                <div className="flex items-center gap-2 min-w-[100px] justify-center">
                  <Timer className="w-5 h-5 text-blue-500" />
                  <span className="text-base sm:text-lg font-semibold whitespace-nowrap">
                    Tiempo: {formatTime(state.timeLeft)}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Contenido principal */}
        <AnimatePresence mode="wait">
          <motion.div
            key={state.gameStarted ? (state.isGameOver ? 'summary' : 'game') : 'start'}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {content}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};
